import type { JSX } from "preact"
export type Element = JSX.Element

import type { NextFrame as NextFrameType } from "../base/Factory"
import { RecordAssign } from "@pdfking/tfunc"
import { computed, effect, signal } from "@preact/signals"
import { h, render } from "preact"
import { useEffect, useRef } from "preact/hooks"
import { CreateReactivityFunctions } from "../base/Factory"
import { RunAllWith } from "../utils/Lifecycle"
import { DefineProperty } from "../utils/Properties"

const constructors = {
  effect,
  set_computed: (
    instance: any,
    key: string,
    get: () => any,
    set: (v: any) => void
  ) => {
    const r = computed(get)
    DefineProperty(instance, key, () => r.value, set)
  },
  set_data: (instance: any, key: string, value: any) => {
    const r = signal(value)
    DefineProperty(
      instance,
      key,
      () => r.value,
      v => (r.value = v)
    )
  },
}

const mountPreact = (renderFn: () => JSX.Element, el: HTMLElement) => {
  render(h(renderFn, { el }), el)
  return el.firstChild!
}

// Component factory that persists instance using useRef
const preactComponentFactory =
  (
    init: (props: any) => any,
    config: {
      render: (children?: JSX.Element[]) => JSX.Element
      mounted?: any
      unmounted?: any
    }
  ) =>
  (props?: any) => {
    const children = props?.children
    const ref = useRef<any>(null)
    if (!ref.current) {
      ref.current = init(props)
    } else {
      RecordAssign(ref.current, props)
    }
    const vnode = config.render.call(ref.current, children)
    useEffect(() => {
      const el = (vnode as any)?.__e as HTMLElement | undefined
      RunAllWith(config.mounted, ref.current, el)
      return () => RunAllWith(config.unmounted, ref.current, el)
    }, [])
    return vnode
  }

export { CreateMixin } from "../base/Factory"
export const NextFrame: NextFrameType = requestAnimationFrame
export const { Init, CreateRenderFunction, Mount } =
  CreateReactivityFunctions<JSX.Element>(
    constructors,
    mountPreact,
    preactComponentFactory,
    () => h("span", null)
  )

export { signal } from "@preact/signals"
// Re-export h and signal for use in other packages
export { h } from "preact"
